import { Form } from '@seiue/ui'
import React from 'react'
import styled from 'styled-components'

import { GrowpIcon } from 'packages/components/GrowpIcon'
import { FieldTypeEnum } from 'packages/sdks-next/growp'
import { customizeCountValidator } from 'packages/utils'

import { getTheme } from '@/utils/theme'

import { ReqColumnWithMetaData, ReqWithMetaData } from '../types'

import { NumDescribe } from './NumDescribe'
import { SelectFormItem } from './SelectFormItem'
import { normFile } from './utils'

/**
 * 问卷 - 复合内容表单
 *
 * @param param - 参数
 * @param param.currentIndex - 当前题目序号
 * @param param.currentQuestion - 当前题目
 * @returns React.ReactNode
 */
export const ProductItem: React.FC<{
  currentIndex: number
  currentQuestion: ReqWithMetaData
}> = ({ currentIndex, currentQuestion }) => {
  const { children } = currentQuestion
  const theme = getTheme()
  if (!children) return null
  return (
    <Wrapper>
      <Form.List
        name={currentQuestion.name || `pv-${currentQuestion.index}`}
        initialValue={[{}]}
      >
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }, index: number) => (
              <ItemWrapper key={key}>
                <ItemTop>
                  <div>{index + 1}.</div>
                  {fields.length < 2 ? null : (
                    <DeleteButton onClick={() => remove(name)}>
                      删除选项
                    </DeleteButton>
                  )}
                </ItemTop>
                {children.map((child: ReqColumnWithMetaData, index: number) => {
                  return (
                    <div key={index}>
                      <ProductLabelWrapper>
                        {child.require && !currentQuestion.require ? (
                          <RequireMark>*</RequireMark>
                        ) : null}
                        <Label>{child.label}</Label>
                        <NumDescribe
                          type={child.type}
                          minCount={child.metadata?.minCount}
                          maxCount={child.metadata?.maxCount}
                          fontSize={14}
                        />
                      </ProductLabelWrapper>
                      <Form.Item
                        {...restField}
                        name={[
                          name,
                          child.name || `pv-${child.index || index}`,
                        ]}
                        valuePropName={
                          child.type === FieldTypeEnum.Attachment
                            ? 'fileList'
                            : undefined
                        }
                        getValueFromEvent={
                          child.type === FieldTypeEnum.Attachment
                            ? normFile
                            : undefined
                        }
                        rules={[
                          {
                            required:
                              currentQuestion.require || !!child.require,
                            message: '此项为必填项',
                          },
                          {
                            validator: (rule, value, callback) =>
                              customizeCountValidator(
                                rule,
                                value,
                                callback,
                                {
                                  max: child.metadata?.maxCount,
                                  min: child.metadata?.minCount,
                                  type: child.type,
                                },
                                true
                              ),
                          },
                        ]}
                      >
                        <SelectFormItem
                          key={index}
                          currentQuestion={child}
                          currentIndex={currentIndex}
                          isProductChild={true}
                          productName={`${
                            currentQuestion.name ||
                            `pv-${currentQuestion.index}`
                          }.${
                            child.name || `pv-${child.index || index}`
                          }.${name}`}
                        />
                      </Form.Item>
                    </div>
                  )
                })}
              </ItemWrapper>
            ))}
            <AddButton onClick={() => add()}>
              <GrowpIcon
                name="Plus"
                color={theme.brand._1}
                size={14}
              />
              <div>添加选项</div>
            </AddButton>
          </>
        )}
      </Form.List>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
`

const ItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.border.greyNormal};
`

const ItemTop = styled.div`
  width: 100%;
  height: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: ${(props) => props.theme.text._1};
  & > div:first-child {
    min-width: 25px;
    height: 22px;
    padding: 0 8px;
    border-radius: 4px;
    text-align: center;
    line-height: 22px;
    background-color: ${(props) => props.theme.background._3};
  }
`

const DeleteButton = styled.div`
  width: 56px;
  height: 22px;
  line-height: 22px;
  font-size: 14px;
  color: #f54545;
  cursor: pointer;
`

const ProductLabelWrapper = styled.div`
  width: 100%;
  height: 22px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 8px;
`

const RequireMark = styled.div`
  width: 14px;
  font-size: 16px;
  padding-top: 6px;
  color: #f54545;
`

const Label = styled.div`
  height: 22px;
  line-height: 22px;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => props.theme.text._1};
`

const AddButton = styled.div`
  width: 75px;
  height: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: ${(props) => props.theme.brand._1};
  cursor: pointer;
`
